<script setup>
// "Our Story" page (#22): CLD presentation with key facts, linking to the
// full timeline (/our-history) and contact. Content reuses the existing
// cld_desc1 intro plus the story_* keys.
const { t } = useI18n();

const paragraphs = computed(() =>
  t('cld_desc1')
    .split('{newline}')
    .map((p) => p.trim())
    .filter(Boolean)
);

// CLD photos alongside the introduction (same tilted-card treatment as the
// homepage banner). Swap the srcs in public/story/ as new shots come in.
const artCards = [
  { class: 'left-0 bottom-[6%] w-[54%] h-[62%] -rotate-6', src: '/story/cldg8.jpg' },
  { class: 'left-[26%] top-0 w-[56%] h-[80%] rotate-2', src: '/story/cldg9.jpg' },
  { class: 'right-0 bottom-[2%] w-[42%] h-[60%] rotate-6', src: '/story/cldg.jpg' },
];

// Team / life-at-CLD strip: real people behind the group.
const teamPhotos = ['/story/cldg12.jpg', '/story/cldg5.jpg', '/story/cldg3.jpg'];

const stats = [
  { icon: '🕰️', key: 'story_stat_1' },
  { icon: '🎮', key: 'story_stat_2' },
  { icon: '🇪🇺', key: 'story_stat_3' },
  { icon: '🏭', key: 'story_stat_4' },
];

const trust = [
  { icon: '✓', title: 'why_cld_1_title', text: 'why_cld_1_text' },
  { icon: '⚡', title: 'why_cld_2_title', text: 'why_cld_2_text' },
  { icon: '📦', title: 'why_cld_3_title', text: 'why_cld_3_text' },
  { icon: '🤝', title: 'why_cld_4_title', text: 'why_cld_4_text' },
];

const route = useRoute();
const ogUrl = computed(() => `https://cld.eu${route.fullPath}`);

useHead(() => ({
  title: `${t('our_story')} — CLD Distribution`,
  meta: [
    { name: 'description', content: t('story_banner_text') },
    { property: 'og:title', content: `${t('our_story')} — CLD Distribution` },
    { property: 'og:description', content: t('story_banner_text') },
    { property: 'og:url', content: ogUrl.value },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'CLD Distribution' },
  ],
  link: [{ rel: 'canonical', href: ogUrl.value }],
  script: [{
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: `${t('our_story')} — CLD Distribution`,
      description: t('story_banner_text'),
      mainEntity: {
        "@type": "Organization",
        name: "CLD Distribution",
        url: "https://cld.eu",
        foundingDate: "1998",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Rue du Grand Champs 14",
          postalCode: "5380",
          addressLocality: "Fernelmont",
          addressCountry: "BE",
        },
      },
    }),
  }],
}));
</script>

<template>
  <div class="container mx-auto px-4 py-10">
    <!-- Hero -->
    <section
      class="rounded-3xl p-8 md:p-12 text-white mb-10 shadow-lg"
      style="background: linear-gradient(120deg, var(--color-primary-700), var(--color-primary) 56%, var(--color-primary-300))"
    >
      <p class="text-xs font-bold uppercase tracking-widest text-[#d9fff3] mb-2">{{ $t('about_cld_title') }}</p>
      <h1 class="text-4xl md:text-5xl font-semibold leading-tight m-0 text-white">{{ $t('our_story') }}</h1>
      <p class="text-white/85 mt-4 max-w-3xl">{{ $t('story_banner_text') }}</p>
    </section>

    <!-- Introduction + photos: text and CLD imagery side by side -->
    <section class="grid md:grid-cols-[1.1fr_.9fr] gap-8 items-center mb-10">
      <div>
        <p v-for="(para, i) in paragraphs" :key="i" class="text-gray-600 mb-4">{{ para }}</p>
      </div>
      <div class="relative h-56 md:h-64" aria-hidden="true">
        <div v-for="(card, i) in artCards" :key="i"
          class="absolute rounded-2xl border-4 border-white shadow-xl overflow-hidden bg-gray-100"
          :class="card.class">
          <NuxtImg :src="card.src" alt="" class="w-full h-full object-cover" format="webp" width="640"
            decoding="async" />
        </div>
      </div>
    </section>

    <!-- Key facts -->
    <section class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
      <div v-for="stat in stats" :key="stat.key" class="bg-white border border-gray-200 rounded-2xl p-5 text-center">
        <div class="text-3xl mb-2">{{ stat.icon }}</div>
        <p class="text-sm font-medium text-gray-900 m-0">{{ $t(stat.key) }}</p>
      </div>
    </section>

    <!-- Group ecosystem -->
    <section class="mb-10 bg-white border border-gray-200 rounded-2xl p-6 md:p-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-3">{{ $t('story_ecosystem_title') }}</h2>
      <p class="text-gray-600 max-w-4xl mb-5">{{ $t('story_ecosystem_text') }}</p>
      <div class="flex flex-wrap gap-2">
        <span v-for="brand in ['CLD Distribution', 'Smartoys', 'Gamecash', 'DLGamer', 'Geedie']" :key="brand"
          class="px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold">
          {{ brand }}
        </span>
      </div>
      <!-- Group infrastructure overview (CLD's own chart) -->
      <div class="mt-6 rounded-xl overflow-hidden border border-gray-100 bg-white">
        <NuxtImg src="/story/cldg11.jpg" :alt="$t('story_ecosystem_title')" class="w-full h-auto"
          format="webp" width="1400" decoding="async" />
      </div>
    </section>

    <!-- Team / life at CLD -->
    <section class="mb-10">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">{{ $t('story_team_title') }}</h2>
      <p class="text-gray-600 max-w-4xl mb-5">{{ $t('story_team_text') }}</p>
      <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div v-for="(photo, i) in teamPhotos" :key="i"
          class="rounded-2xl overflow-hidden border border-gray-100 bg-white aspect-[4/3]">
          <NuxtImg :src="photo" alt="" class="w-full h-full object-cover" format="webp" width="600"
            decoding="async" />
        </div>
      </div>
    </section>

    <!-- Partnerships -->
    <section class="mb-10">
      <h2 class="text-2xl font-semibold text-gray-900 mb-3">{{ $t('svc_partners_title') }}</h2>
      <p class="text-gray-600 max-w-4xl">{{ $t('svc_partners_text') }}</p>
    </section>

    <!-- Why CLD -->
    <section class="mb-10">
      <p class="text-xs font-bold uppercase tracking-widest text-[#008556] mb-1">{{ $t('why_cld_kicker') }}</p>
      <h2 class="text-2xl md:text-3xl font-semibold text-gray-900 mb-5">{{ $t('why_cld_title') }}</h2>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <article v-for="item in trust" :key="item.title" class="bg-white border border-gray-200 rounded-2xl p-5">
          <div class="trust-icon-chip w-10 h-10 rounded-xl bg-[#e9f6f0] text-[#008556] grid place-items-center text-lg mb-3">
            {{ item.icon }}
          </div>
          <h3 class="text-base font-medium text-gray-900 mb-1.5">{{ $t(item.title) }}</h3>
          <p class="text-xs text-gray-500 m-0">{{ $t(item.text) }}</p>
        </article>
      </div>
    </section>

    <!-- CTAs -->
    <section class="flex flex-wrap gap-3">
      <NuxtLinkLocale to="/our-history" class="cld-btn cld-btn-primary">
        {{ $t('story_history_cta') }} →
      </NuxtLinkLocale>
      <NuxtLinkLocale to="/contact-us" class="cld-btn cld-btn-outline">
        {{ $t('story_contact_cta') }}
      </NuxtLinkLocale>
    </section>
  </div>
</template>

<style scoped>
/* Styles handled in global tailwind.css; dark theme via utility overrides in dark.css */
</style>
